import { oids } from "@/configs/Settings";
import { normalizeCpf } from "src-next/shared/utils/cpf";

import type { CrmPatientSummary } from "../types";

function pickOfficialName(patient: fhir5.Patient): string {
  const official = patient.name?.find((n) => n.use === "official")?.text?.trim();
  if (official) return official;
  return patient.name?.[0]?.text?.trim() ?? "Sem nome";
}

function pickUsualName(patient: fhir5.Patient): string | undefined {
  const usual = patient.name?.find((n) => n.use === "usual")?.text?.trim();
  return usual || undefined;
}

function pickPhone(patient: fhir5.Patient): string | undefined {
  return (
    patient.telecom?.find((t) => t.system === "phone")?.value?.trim() ||
    undefined
  );
}

function pickEmail(patient: fhir5.Patient): string | undefined {
  return (
    patient.telecom?.find((t) => t.system === "email")?.value?.trim() ||
    undefined
  );
}

/** CPF via OID ou identifier TAX (paridade com patientService / CostumerRelationship). */
function pickTaxIdentifier(patient: fhir5.Patient): string | undefined {
  const cpfOid = oids.cpf;
  for (const identifier of patient.identifier ?? []) {
    const value = identifier.value?.trim();
    if (!value) continue;
    const system = identifier.system ?? "";
    const isTax = identifier.type?.coding?.some((c) => c.code === "TAX");
    if (system.includes(cpfOid) || system.endsWith(cpfOid) || isTax) {
      const digits = normalizeCpf(value);
      return digits.length === 11 ? digits : value;
    }
  }
  if (patient.id?.includes("-")) {
    const suffix = patient.id.split("-").pop()?.trim();
    if (suffix && normalizeCpf(suffix).length === 11) return normalizeCpf(suffix);
  }
  return undefined;
}

function normalizeGender(
  gender: fhir5.Patient["gender"],
): CrmPatientSummary["gender"] {
  if (
    gender === "male" ||
    gender === "female" ||
    gender === "other" ||
    gender === "unknown"
  ) {
    return gender;
  }
  return undefined;
}

/** Mapeia recurso Patient FHIR para resumo usado na UI de comunicações. */
export function mapFhirPatientToCrmPatientSummary(
  patient: fhir5.Patient,
): CrmPatientSummary | null {
  const patientId = patient.id?.trim();
  if (!patientId) return null;

  return {
    patientId,
    officialName: pickOfficialName(patient),
    usualName: pickUsualName(patient),
    birthDate: patient.birthDate ?? "",
    gender: normalizeGender(patient.gender),
    phone: pickPhone(patient),
    email: pickEmail(patient),
    taxIdentifierValue: pickTaxIdentifier(patient),
    lastAppointmentAt: undefined,
  };
}

/** Converte entries do PatientOrganizationContext para CrmPatientSummary[]. */
export function mapPatientEntriesToCrmPatientSummaries(
  entries: fhir5.BundleEntry<fhir5.Patient>[] | undefined,
): CrmPatientSummary[] {
  if (!entries?.length) return [];
  const result: CrmPatientSummary[] = [];
  for (const entry of entries) {
    const resource = entry.resource;
    if (!resource || resource.resourceType !== "Patient") continue;
    const mapped = mapFhirPatientToCrmPatientSummary(resource);
    if (mapped) result.push(mapped);
  }
  return result;
}
